<template>
  <div>
    <div class="sidebar-heading d-flex align-center justify-start mb-5">
      <v-icon class="mr-2" color="primary" :size="defaultIconSize">
        $page
      </v-icon>
      <h3 class="regular-action-title sidebar-heading mb-0">
        {{ $t("chapter.image.imageSettings") }}
      </h3>
    </div>
    <v-expansion-panels
      flat
      class="chapter-details-expansion-panels"
      multiple
      :value="[0, 1]"
    >
      <!-- SELECT IMAGE -->
      <v-expansion-panel class="mb-3 pa-0">
        <v-expansion-panel-header
          expand-icon="$chevronDown"
          class="d-flex align-center justify-start px-3 py-2"
        >
          <div class="ml-2">{{ $t("chapter.image.imageContents") }}</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content v-if="editor" class="pa-2 pb-5">
          <v-hover>
            <template v-slot:default="{ hover }">
              <div
                :class="
                  'select-media-container' + (!imageUrl ? ' placeholder' : '')
                "
                :style="`border-radius: ${radius}%`"
              >
                <v-img v-if="imageUrl" class="image-preview" :src="imageUrl" />
                <div :class="imageUrl ? null : 'py-15'">
                  <v-fade-transition>
                    <v-overlay
                      content-class="media-object-upload-overlay-content"
                      absolute
                      v-show="
                        (hover && !loading) || !imageUrl || imageUrl == ''
                      "
                      :color="imageUrl ? 'black' : 'transparent'"
                    >
                      <MediaObjectUpload
                        v-model="uploadUrl"
                        :addText="$t('chapter.image.chooseImage')"
                        :changeText="$t('chapter.image.chooseImage')"
                        @error="setErrorMessage"
                        @loading="loading = $event"
                        :errorTooLargeMessage="
                          $t('register.imageTooLarge', {
                            maxImageSize: MAX_IMAGE_SIZE_MB,
                          })
                        "
                        :maxSizeBytes="MAX_IMAGE_SIZE_BYTES"
                        :formats="IMAGE_FORMATS"
                      />
                    </v-overlay>
                  </v-fade-transition>
                </div>
              </div>
            </template>
          </v-hover>
          <UploadErrorMessage :errorMessage="errorMessage" :loading="loading" />
          <div class="text-left my-3" v-if="loading">
            {{ $t("forms.uploading") }}
            <v-progress-linear
              class="mt-3"
              indeterminate
              background-color="tertiaryBackground"
              rounded
              color="primaryLight"
              height="6px"
            />
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- IMAGE OPTIONS -->
      <v-expansion-panel class="pa-0">
        <v-expansion-panel-header
          expand-icon="$chevronDown"
          class="d-flex align-center justify-start px-3 py-2"
        >
          <div class="ml-2">{{ $t("chapter.image.imageSettings") }}</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pa-2 pb-5">
          <div class="d-flex mb-5">
            <v-btn
              @click="setFitMode"
              depressed
              class="fit-button rounded-lg mr-1"
              :style="`
                background: ${fitModeActive ? 'var(--v-primary-base)' : gradients.backgroundAccent};
                border: 1px solid ${fitModeActive ? 'var(--v-primary-base)' : 'var(--v-lightBackground-base)'};
                color: var(--v-${fitModeActive ? 'textOnPrimary' : 'textOnAccent'}-base);
              `"
            >
              <v-icon class="mr-2">$imageResize</v-icon>
              {{ $t("chapter.image.fitMode") }}
            </v-btn>
            <v-btn
              @click="setFullMode"
              depressed
              class="fit-button rounded-lg ml-1"
              :style="`
                background: ${fullModeActive ? 'var(--v-primary-base)' : gradients.backgroundAccent};
                border: 1px solid ${fullModeActive ? 'var(--v-primary-base)' : 'var(--v-lightBackground-base)'};
                color: var(--v-${fullModeActive ? 'textOnPrimary' : 'textOnAccent'}-base);
              `"
            >
              <v-icon class="mr-2">$fullscreen</v-icon>
              {{ $t("chapter.image.fullMode") }}
            </v-btn>
          </div>
          <h4 class="radius-header mb-1">
            {{ $t("chapter.image.cornerRadius") }}: <span>{{ radius }}%</span>
          </h4>
          <v-slider
            min="0"
            max="50"
            color="primaryAction"
            hide-details
            class="story-city-slider mx-0"
            track-color="primaryExtraLight"
            track-fill-color="primaryFaded"
            @change="updateRadiusAttr"
            v-model="radius"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import UploadErrorMessage from "./UploadErrorMessage.vue";
const EXTENSION_NAME = "image";
import MediaObjectUpload from "@/components/MediaObjectUpload";
import {
  IMAGE_FORMATS,
  MAX_IMAGE_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_MEGABYTES,
  GRADIENTS,
  DEFAULT_ICON_SIZE,
} from "@/const";
import { AUTH_TOKEN } from "@/apollo/vue-apollo";

export default {
  props: {
    editor: Object,
  },
  components: {
    MediaObjectUpload,
    UploadErrorMessage,
  },
  data() {
    return {
      loading: false,
      uploadUrl: null,
      errorMessage: "",
      startRadius: this.editor.getAttributes(EXTENSION_NAME).radius,
      radius: 0,
    };
  },
  mounted() {
    this.radius = this.editor.getAttributes(EXTENSION_NAME).radius;
    if (this.editor) {
      this.editor.on("selectionUpdate", () =>
        this.handleEditorSelectionUpdate()
      );
    }
  },
  methods: {
    setErrorMessage(message) {
      this.errorMessage = message;
    },
    handleEditorSelectionUpdate() {
      if (this.isActive) {
        this.radius = this.editor.getAttributes(EXTENSION_NAME).radius;
      }
    },
    updateRadiusAttr(newValue) {
      this.editor.chain().focus().setRadius(newValue).run();
    },
    updateImageSrcAttr(event) {
      this.editor.chain().focus().setSrc(event).run();
    },
    setFullMode() {
      this.editor.chain().focus().setFullMode().run();
    },
    setFitMode() {
      this.editor.chain().focus().setFitMode().run();
    },
    align(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },
  },
  computed: {
    MAX_IMAGE_SIZE_MB() {
      return MAX_IMAGE_UPLOAD_MEGABYTES;
    },
    MAX_IMAGE_SIZE_BYTES() {
      return MAX_IMAGE_UPLOAD_BYTES;
    },
    IMAGE_FORMATS() {
      return IMAGE_FORMATS;
    },
    jwt() {
      return localStorage.getItem(AUTH_TOKEN);
    },
    isActive() {
      return this.editor.isActive(EXTENSION_NAME);
    },
    imageUrl() {
      return this.editor.getAttributes(EXTENSION_NAME).src;
    },
    fullModeActive() {
      return this.editor.getAttributes(EXTENSION_NAME).fullMode;
    },
    fitModeActive() {
      return this.editor.getAttributes(EXTENSION_NAME).fitMode;
    },
    gradients() {
      return GRADIENTS;
    },
    defaultIconSize() {
      return DEFAULT_ICON_SIZE;
    },
  },
  watch: {
    uploadUrl(newValue) {
      this.editor.chain().focus().setImageSrc(newValue).run();
    },
    imageUrl(newValue) {
      this.uploadUrl = newValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.image-preview {
  height: auto;
  max-width: 100%;
  width: auto;
}

.image-upload-container {
  background-color: var(--v-background-base);
  width: 100%;
  border: 2px dashed #949191;
}

.fit-button {
  flex: 1;
}

.radius-header {
  color: var(--v-secondaryText-base);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: $font-spacing;
}

::v-deep .radius-header span {
  color: var(--Secondary-Blue-110, #1879B5);
  font-size: 16px;
  font-weight: 700;
}
</style>
